import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import CommentEditor from "../../components/commentEditor/CommentEditor";

import DOMPurify from "dompurify";

import ReactTimeAgo from "react-time-ago";
import Loader from "../../components/loader/Loader";
import AnimatedPage from "../../components/animatedPage/AnimatedPage";

import {
  BellIcon,
  DeviceMobileIcon,
  MailIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/outline";

import { useAppSelector } from "../../app/hooks";
import { selectUser } from "../userProfile/userSlice";

import { useAppDispatch } from "../../app/hooks";
import { resetCommentsCount } from "./jobStats/jobStatsSlice";

import * as api from "./apiService";
import { toast } from "react-toastify";

import DeleteCommentModal from "./DeleteJobCommentModal";
import JobPhotosWorkspace from "./JobPhotosWorkspace";

const sanitizeHtml = (html) =>
  DOMPurify.sanitize(html || "", {
    USE_PROFILES: { html: true },
  });

const sanitizeCommentHtml = (html) => {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = sanitizeHtml(html);

  while (wrapper.firstChild) {
    const firstNode = wrapper.firstChild;
    if (firstNode.nodeType === Node.TEXT_NODE && !firstNode.textContent.trim()) {
      firstNode.remove();
      continue;
    }

    if (
      firstNode.nodeType === Node.ELEMENT_NODE &&
      (firstNode.tagName === "BR" ||
        (!firstNode.textContent.trim() &&
          !firstNode.querySelector("img, video, audio, iframe")))
    ) {
      firstNode.remove();
      continue;
    }

    break;
  }

  return wrapper.innerHTML;
};

function NotificationOption({ id, checked, onChange, icon: Icon, title, description, children }) {
  return (
    <div className="py-3">
      <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
        <input
          id={id}
          name={id}
          checked={checked}
          onChange={onChange}
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#E3222A] focus:ring-[#E3222A]"
        />
        <Icon className="mt-0.5 h-4 w-4 flex-none text-slate-400" aria-hidden="true" />
        <span>
          <span className="block text-sm font-bold text-slate-700">{title}</span>
          <span className="block text-xs text-slate-500">{description}</span>
        </span>
      </label>
      {children}
    </div>
  );
}

const JobComments = ({ embedded = false, photosCount = 0, jobStatus = "", initialTab = "notes" }) => {
  const { jobId } = useParams();
  const bottomRef = useRef(null);
  const location = useLocation();
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createCommentLoading, setCreateCommentLoading] = useState(false);
  const [sendSMS, setSendSMS] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [isAdminOnly, setIsAdminOnly] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendEmailToProjectManager, setSendEmailToProjectManager] =
    useState(false);
  const [sendEmailToFollowers, setSendEmailToFollowers] = useState(false);

  const [isDeleteCommentModalOpen, setDeleteCommentModalOpen] = useState(false);
  const [commentToBeDeleted, setCommentToBeDeleted] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [currentPhotosCount, setCurrentPhotosCount] = useState(photosCount);
  const [photoToolbarTarget, setPhotoToolbarTarget] = useState(null);

  const currentUser = useAppSelector(selectUser);

  const [userEmails, setUserEmails] = useState([]);
  const [projectManagerEmails, setProjectManagerEmails] = useState([]);
  const [followerEmails, setFollowerEmails] = useState([]);

  const dispatch = useAppDispatch();

  const isNotesAdmin = currentUser.isAdmin || currentUser.isSuperUser;
  const canReadRestrictedNotes = isNotesAdmin || currentUser.isInternalCoordinator;
  const isCustomerOrVendor = !isNotesAdmin && (
    currentUser.isCustomer || currentUser.isProjectManager || currentUser.isExternalProjectManager
  );
  const canChooseVisibility =
    !isCustomerOrVendor && (
      currentUser.isAdmin ||
      currentUser.isSuperUser ||
      currentUser.isAccountManager
    );
  const canSendSMS = !isCustomerOrVendor && (canChooseVisibility || currentUser.isInternalCoordinator);
  const selectedEmailRecipientCount =
    (sendEmail ? userEmails.filter((email) => email.selected).length : 0) +
    (sendEmailToProjectManager
      ? projectManagerEmails.filter((email) => email.selected).length
      : 0) +
    (sendEmailToFollowers
      ? followerEmails.filter((email) => email.selected).length
      : 0);
  const notificationSummary = [
    sendSMS && "SMS",
    selectedEmailRecipientCount > 0 &&
      `${selectedEmailRecipientCount} email ${
        selectedEmailRecipientCount === 1 ? "recipient" : "recipients"
      }`,
  ]
    .filter(Boolean)
    .join(" · ");

  useEffect(() => {
    if (isCustomerOrVendor) setIsPublic(true);
  }, [isCustomerOrVendor]);

  useEffect(() => {
    getComments();
    dispatch(resetCommentsCount());
  }, []);

  const getUserEmails = async () => {
    try {
      const { data } = await api.getUserJobEmails(Number(jobId));

      data.emails = data.emails.map((el) => ({ ...el, selected: true }));
      data.project_manager_emails = data.project_manager_emails.map((el) => ({
        ...el,
        selected: true,
      }));
      data.follower_emails = data.follower_emails.map((el) => ({
        ...el,
        selected: true,
      }));

      setUserEmails(data.emails);
      setProjectManagerEmails(data.project_manager_emails);
      setFollowerEmails(data.follower_emails);
    } catch (err) {
      toast.error("Unable to fetch user info");
    }
  };

  const getComments = async () => {
    setLoading(true);

    try {
      const { data } = await api.getJobComments(jobId);
      setComments(data.results.filter((note) =>
        (!note.is_admin_only || canReadRestrictedNotes) && (!currentUser.isCustomer || canReadRestrictedNotes || note.is_public)
      ));
    } catch (error) {}

    setLoading(false);
  };

  const handleToggleDeleteCommentModal = (comment) => {
    if (comment) {
      setCommentToBeDeleted(comment);
    } else {
      setCommentToBeDeleted(null);
    }

    setDeleteCommentModalOpen(!isDeleteCommentModalOpen);
  };

  const deleteComment = async () => {
    await api.deleteComment(commentToBeDeleted.id);

    setDeleteCommentModalOpen(false);

    const updatedComments = comments.filter(
      (s) => s.id !== commentToBeDeleted.id,
    );

    setComments(updatedComments);
    setCommentToBeDeleted(null);
  };

  const handleSetSendSMS = () => setSendSMS(!sendSMS);
  const handleSetIsPublic = () => setIsPublic(!isPublic);
  const chooseVisibility = (visibility) => {
    setIsPublic(visibility === "public");
    setIsAdminOnly(visibility === "admin_only");
    if (visibility === "admin_only") {
      setSendSMS(false);
      setSendEmail(false);
      setSendEmailToProjectManager(false);
      setSendEmailToFollowers(false);
    }
  };

  const handleSetSendEmail = () => {
    const fetchEmails = !sendEmail;
    setSendEmail(!sendEmail);

    if (fetchEmails) {
      getUserEmails();
    }
  };

  const handleSetSendEmailToProjectManager = () => {
    const fetchEmails = !sendEmailToProjectManager;
    setSendEmailToProjectManager(!sendEmailToProjectManager);

    if (fetchEmails) {
      getUserEmails();
    }
  };

  const handleSetSendFollowersEmail = () => {
    const fetchEmails = !sendEmailToFollowers;
    setSendEmailToFollowers(!sendEmailToFollowers);

    if (fetchEmails) {
      getUserEmails();
    }
  };

  const handleUserEmailChange = (email) => {
    const tagsUpdated = userEmails.map((el) =>
      el.email === email ? { ...el, selected: !el.selected } : el,
    );
    setUserEmails(tagsUpdated);
  };

  const handleProjectManagerEmailChange = (email) => {
    const tagsUpdated = projectManagerEmails.map((el) =>
      el.email === email ? { ...el, selected: !el.selected } : el,
    );
    setProjectManagerEmails(tagsUpdated);
  };

  const handleFollowerEmailChange = (email) => {
    const tagsUpdated = followerEmails.map((el) =>
      el.email === email ? { ...el, selected: !el.selected } : el,
    );
    setFollowerEmails(tagsUpdated);
  };

  const createJobComment = async () => {
    const selectedEmails = userEmails.filter((el) => el.selected);
    const selectedProjectManagerEmails = projectManagerEmails.filter(
      (el) => el.selected,
    );
    const selectedFollowerEmails = followerEmails.filter((el) => el.selected);

    if (sendEmail && selectedEmails.length === 0) {
      alert("Select at least one email to send the email to.");
      return;
    }

    if (
      sendEmailToProjectManager &&
      selectedProjectManagerEmails.length === 0
    ) {
      alert("Select at least one project manager email to send the email to.");
      return;
    }

    if (sendEmailToFollowers && selectedFollowerEmails.length === 0) {
      alert("Select at least one follower email to send the email to.");
      return;
    }

    const plainComment = stripHtml(comment);

    const request = {
      comment: plainComment,
      commentHtml: comment,
      sendSMS,
      isPublic,
      isAdminOnly,
      sendEmail,
      sendEmailToProjectManager,
      sendEmailToFollowers,
      emails: selectedEmails.map((el) => el.email),
      projectManagerEmails: selectedProjectManagerEmails.map((el) => el.email),
      followerEmails: selectedFollowerEmails.map((el) => el.email),
      first_name: currentUser.first_name,
      last_name: currentUser.last_name,
      email: "email",
    };

    setCreateCommentLoading(true);

    try {
      const { data } = await api.createJobComment(jobId, request);

      const updatedComments = [...comments, data];

      setComments(updatedComments);
      setComment("");
      setCreateCommentLoading(false);

      setSendSMS(false);
      setSendEmail(false);
      setIsPublic(isCustomerOrVendor);
      setIsAdminOnly(false);
      setSendEmailToProjectManager(false);
      setSendEmailToFollowers(false);

      toast.success("Comment created!");
    } catch (error) {
      setCreateCommentLoading(false);

      if (error.response?.status === 403) {
        alert("You do not have permission to create a comment for this job.");
      } else {
        alert("Unable to create comment.");
      }
    }
  };

  function stripHtml(html) {
    if (!html) return "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Replace <br> and block elements with newlines before extracting text
    tempDiv.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));

    tempDiv.querySelectorAll("p, div").forEach((el) => {
      if (el.textContent.trim() !== "") {
        el.insertAdjacentText("beforebegin", "\n");
      }
    });

    return tempDiv.textContent
      .replace(/\n\s*\n/g, "\n") // collapse multiple newlines
      .trim();
  }

  const toggleCommentVisibility = async (comment, visibility) => {
    try {
      const { data } = await api.updateComment(comment.id, visibility ? { visibility } : undefined);

      const updatedComments = comments.map((c) => {
        if (c.id === comment.id) {
          return { ...c, ...data };
        }

        return c;
      });

      setComments(updatedComments);
    } catch (error) {
      alert("Unable to update comment");
    }
  };

  const hasComment = stripHtml(comment).length > 0;

  const renderRecipientPicker = (emails, prefix, onChange, emptyMessage) => {
    if (emails.length === 0) {
      return <p className="mt-3 pl-11 text-xs text-slate-500">{emptyMessage}</p>;
    }

    return (
      <div className="ml-11 mt-3 space-y-2 rounded-lg bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
        <p className="text-xs font-semibold text-slate-600">Select recipients</p>
        {emails.map((email, index) => (
          <label key={email.email} htmlFor={`${prefix}-${index}`} className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
            <input
              id={`${prefix}-${index}`}
              checked={email.selected}
              onChange={() => onChange(email.email)}
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-[#E3222A] focus:ring-[#E3222A]"
            />
            {email.email}
          </label>
        ))}
      </div>
    );
  };

  return (
    <AnimatedPage>
      {loading && <Loader />}

      {!loading && (
        <div
          className={`${location.pathname.includes("comments") ? "mt-8" : ""} relative overflow-hidden bg-white ${
            embedded
              ? "rounded-xl border border-slate-200 shadow-sm"
              : "rounded-lg border border-gray-300"
          }`}
        >
          <div
            data-testid={embedded ? "job-notes-photos-header" : undefined}
            className={`flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-3 ${
              embedded ? "bg-white" : "bg-gray-100"
            }`}
          >
            {embedded ? (
              <div className="flex h-10 items-center gap-5" role="tablist" aria-label="Job notes and photos">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "notes"}
                  onClick={() => setActiveTab("notes")}
                  className={`relative inline-flex h-full items-center gap-1.5 border-b-2 text-sm transition ${activeTab === "notes" ? "border-slate-950 font-bold text-slate-950" : "border-transparent font-semibold text-slate-500 hover:text-slate-900"}`}
                >
                  Notes
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${activeTab === "notes" ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-500"}`}>
                    {comments.length}
                  </span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "photos"}
                  onClick={() => setActiveTab("photos")}
                  className={`relative inline-flex h-full items-center gap-1.5 border-b-2 text-sm transition ${activeTab === "photos" ? "border-slate-950 font-bold text-slate-950" : "border-transparent font-semibold text-slate-500 hover:text-slate-900"}`}
                >
                  Photos
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${activeTab === "photos" ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-500"}`}>
                    {currentPhotosCount}
                  </span>
                </button>
              </div>
            ) : (
              <h3 className="text-base font-bold uppercase leading-7 text-gray-900">
                Comments
              </h3>
            )}
            {(!embedded || activeTab === "notes") && <div className="ml-auto text-right">
              <button
                type="button"
                onClick={() => getComments()}
                disabled={loading}
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Refresh
              </button>
            </div>}
            {embedded && activeTab === "photos" && (
              <div ref={setPhotoToolbarTarget} className="ml-auto" />
            )}
          </div>

          {(!embedded || activeTab === "notes") && <div>
            <div
              className="flex flex-col p-4 pr-1"
              style={{ maxHeight: "450px", overflowY: "auto" }}
            >
              {comments.length === 0 && (
                <>
                  <div className="mt-2 flex justify-center text-md font-medium text-gray-700">
                    No comments found.
                  </div>
                  <p className="flex justify-center text-md text-gray-500">
                    Be the first to comment!
                  </p>
                </>
              )}

              <ul className="space-y-5">
                {comments.map((comment) => (
                  <li key={comment.id}>
                    <div className="flex gap-3">
                      <div className="flex-none">
                        <div className="w-9 text-center">
                          {comment.author.profile?.avatar ? (
                            <img
                              className="h-9 w-9 rounded-full"
                              src={comment.author.profile.avatar}
                              alt=""
                            />
                          ) : (
                            <div
                              className="grid h-9 w-9 place-items-center rounded-full bg-[#E3222A] text-[10px] font-black text-white"
                              style={{
                                background: comment.integration_first_name
                                  ? "#0f172a"
                                  : "#E3222A",
                              }}
                            >
                              {comment.author.username
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="min-w-0 flex-1 pr-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            <strong className="font-bold text-slate-900">
                              {comment.integration_first_name
                                ? comment.integration_first_name
                                : comment.author.first_name}{" "}
                              {comment.integration_last_name
                                ? comment.integration_last_name
                                : comment.author.last_name}
                            </strong>
                            {isNotesAdmin ? (
                              <select
                                aria-label={`Visibility for note ${comment.id}`}
                                value={comment.is_admin_only ? "admin_only" : comment.is_public ? "public" : "internal"}
                                onChange={(event) => toggleCommentVisibility(comment, event.target.value)}
                                className={`max-w-full rounded-full border py-0.5 pl-2 pr-7 text-xs font-medium ${
                                  comment.is_admin_only ? "border-violet-200 bg-violet-50 text-violet-700" :
                                  comment.is_public ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                                  "border-red-200 bg-red-50 text-red-700"
                                }`}
                              >
                                <option value="public">Public</option>
                                <option value="internal">Internal</option>
                                <option value="admin_only">Admins & coordinators</option>
                              </select>
                            ) : comment.is_admin_only ? (
                              <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
                                Admins &amp; coordinators
                              </span>
                            ) : !isCustomerOrVendor && (
                              <button
                                type="button"
                                disabled={!canChooseVisibility}
                                onClick={() => canChooseVisibility && toggleCommentVisibility(comment)}
                                className={`inline-flex h-5 items-center rounded-full border px-2 py-0.5 text-[10px] font-medium disabled:cursor-default ${
                                  comment.is_public
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-red-200 bg-red-50 text-red-700"
                                }`}
                              >
                                {comment.is_public ? "Public" : "Internal"}
                              </button>
                            )}
                            <span className="text-xs font-medium text-slate-400">
                              <ReactTimeAgo
                                date={new Date(comment.created)}
                                locale="en-US"
                                timeStyle="twitter"
                              />
                            </span>
                          </div>

                          {canChooseVisibility && (!comment.is_admin_only || isNotesAdmin) && (
                            <button
                              type="button"
                              onClick={() => handleToggleDeleteCommentModal(comment)}
                              className="inline-flex h-5 items-center rounded-md px-2 text-[11px] font-semibold leading-none text-slate-500 transition hover:bg-red-50 hover:text-[#E3222A]"
                              aria-label="Delete comment"
                            >
                              Delete
                            </button>
                          )}
                        </div>

                        <div className="mt-1 text-sm leading-6 text-slate-700">
                          {comment.comment_html ? (
                            <div
                              data-testid={`comment-content-${comment.id}`}
                              className="comment-content [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-0 [&_p+_p]:mt-2"
                              dangerouslySetInnerHTML={{
                                __html: sanitizeCommentHtml(comment.comment_html),
                              }}
                            />
                          ) : (
                            <p className="m-0">{comment.comment}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div ref={bottomRef} />
            </div>

            {embedded ? (
              <div className="mx-5 mb-5 mt-1 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:mx-6">
                <div className="comment-editor job-comment-editor w-full overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <CommentEditor
                    value={comment}
                    onChange={setComment}
                    placeholder="Write a note or update…"
                    aria-label="Write a note"
                  />
                </div>

                {hasComment && (
                  <div className="mt-3 space-y-3 border-t border-slate-200 pt-3">
                    {canChooseVisibility && (
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-extrabold uppercase tracking-[0.08em] text-slate-500">Visibility</p>
                          <p className="mt-0.5 text-xs text-slate-500">Choose who can read this note.</p>
                        </div>
                        <div className="flex rounded-lg border border-slate-200 bg-white p-1">
                          <button
                            type="button"
                            onClick={() => chooseVisibility("public")}
                            aria-pressed={isPublic}
                            className={`rounded-md px-3 py-1.5 text-xs font-bold ${isPublic ? "bg-slate-900 text-white" : "text-slate-500"}`}
                          >
                            Public
                          </button>
                          <button
                            type="button"
                            onClick={() => chooseVisibility("internal")}
                            aria-pressed={!isPublic && !isAdminOnly}
                            className={`rounded-md px-3 py-1.5 text-xs font-bold ${!isPublic && !isAdminOnly ? "bg-slate-900 text-white" : "text-slate-500"}`}
                          >
                            Internal
                          </button>
                          {isNotesAdmin && (
                            <button
                              type="button"
                              onClick={() => chooseVisibility("admin_only")}
                              aria-pressed={isAdminOnly}
                              className={`rounded-md px-3 py-1.5 text-xs font-bold ${isAdminOnly ? "bg-violet-700 text-white" : "text-slate-500"}`}
                            >
                              Admins & coordinators
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {isAdminOnly && (
                      <p className="text-xs leading-5 text-violet-700">
                        Only admins and internal coordinators can read this note. It stays off closeout links and sends no email, SMS, push, or integration notifications.
                      </p>
                    )}
                    {!isAdminOnly && (canSendSMS || canChooseVisibility) && (
                      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <BellIcon className="h-4 w-4 flex-none text-[#E3222A]" aria-hidden="true" />
                            <div>
                              <p className="text-sm font-extrabold text-slate-800">Notifications</p>
                              <p className="text-xs text-slate-500">Select recipients before posting.</p>
                            </div>
                          </div>
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                            {selectedEmailRecipientCount} email {selectedEmailRecipientCount === 1 ? "recipient" : "recipients"}
                          </span>
                        </div>

                        <div className="divide-y divide-slate-100 px-3">
                          {canSendSMS && (
                            <NotificationOption
                              id="sendSMS-overview"
                              checked={sendSMS}
                              onChange={handleSetSendSMS}
                              icon={DeviceMobileIcon}
                              title="Send SMS"
                              description="Notify assigned project managers."
                            />
                          )}
                          {canChooseVisibility && (
                            <>
                              <NotificationOption
                                id="email-overview"
                                checked={sendEmail}
                                onChange={handleSetSendEmail}
                                icon={MailIcon}
                                title="Email Job Creator"
                                description="Notify the user who created this job."
                              >
                                {sendEmail && renderRecipientPicker(
                                  userEmails,
                                  "overview-user-email",
                                  handleUserEmailChange,
                                  "No email is specified for the user who created this job.",
                                )}
                              </NotificationOption>
                              <NotificationOption
                                id="email-pm-overview"
                                checked={sendEmailToProjectManager}
                                onChange={handleSetSendEmailToProjectManager}
                                icon={MailIcon}
                                title="Email Assigned Project Managers"
                                description="Notify the PMs assigned to this job."
                              >
                                {sendEmailToProjectManager && renderRecipientPicker(
                                  projectManagerEmails,
                                  "overview-pm-email",
                                  handleProjectManagerEmailChange,
                                  "No emails are specified for assigned project managers.",
                                )}
                              </NotificationOption>
                              <NotificationOption
                                id="email-followers-overview"
                                checked={sendEmailToFollowers}
                                onChange={handleSetSendFollowersEmail}
                                icon={MailIcon}
                                title="Email Followers"
                                description="Notify the selected job followers."
                              >
                                {sendEmailToFollowers && renderRecipientPicker(
                                  followerEmails,
                                  "overview-follower-email",
                                  handleFollowerEmailChange,
                                  "No emails are specified for job followers.",
                                )}
                              </NotificationOption>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    <div className={`flex flex-wrap items-center gap-3 ${isCustomerOrVendor ? "justify-end" : "justify-between"}`}>
                      {!isCustomerOrVendor && (
                        <span className="text-xs font-semibold text-slate-500">
                          {isAdminOnly ? "Admins & coordinators" : isPublic ? "Public" : "Internal"} note · {notificationSummary || "No notifications"}
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={createCommentLoading}
                        onClick={() => createJobComment()}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#E3222A] px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-[#E3222A] focus:ring-offset-2 disabled:opacity-50"
                      >
                        <PaperAirplaneIcon className="h-3.5 w-3.5 rotate-45" aria-hidden="true" />
                        {createCommentLoading ? "Posting…" : "Post note"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
            <div className="bg-gray-50 px-3 py-6 sm:px-4">
              <div className="space-y-5">
                <div className="w-full">
                  <div className="comment-editor w-full rounded-lg border border-gray-200 bg-white shadow-sm">
                    <CommentEditor
                      value={comment}
                      onChange={setComment}
                      placeholder="Write your comment here..."
                    />
                  </div>
                </div>

                {comment.length > 0 && (
                  <div className="space-y-5">
                    <div className="flex flex-col gap-4 border-t border-gray-200 pt-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="order-2 sm:order-1 flex-1 pr-0 sm:pr-8">
                        {(currentUser.isAdmin ||
                          currentUser.isSuperUser ||
                          currentUser.isAccountManager ||
                          currentUser.isInternalCoordinator) && (
                          <div className="flex items-start">
                            <div className="mt-0.5 flex h-5 items-center">
                              <input
                                id="sendSMS"
                                name="sendSMS"
                                checked={sendSMS}
                                onChange={handleSetSendSMS}
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                              />
                            </div>
                            <div className="ml-3">
                              <label
                                htmlFor="sendSMS"
                                className="text-md font-medium text-gray-700"
                              >
                                Send SMS
                              </label>
                              <p className="text-sm text-gray-500">
                                Notify assigned project managers.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="order-1 sm:order-2 w-full sm:w-auto sm:shrink-0">
                        <button
                          type="button"
                          disabled={createCommentLoading}
                          onClick={() => createJobComment()}
                          className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-md text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        >
                          {createCommentLoading ? "....." : "Post"}
                        </button>
                      </div>
                    </div>

                    {(currentUser.isAdmin ||
                      currentUser.isSuperUser ||
                      currentUser.isAccountManager) && (
                      <div className="border-t border-gray-200 pt-4">
                        <div className="flex items-center">
                          <div className="flex h-5 items-center">
                            <input
                              id="public"
                              name="public"
                              checked={isPublic}
                              onChange={handleSetIsPublic}
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                          </div>
                          <div className="ml-3 text-md">
                            <label
                              htmlFor="public"
                              className="font-medium text-gray-700"
                            >
                              Public
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    {(currentUser.isAdmin ||
                      currentUser.isSuperUser ||
                      currentUser.isAccountManager) && (
                      <>
                        <div className="border-t border-gray-200 pt-4">
                          <div className="flex items-start">
                            <div className="mt-0.5 flex h-5 items-center">
                              <input
                                id="email"
                                name="email"
                                checked={sendEmail}
                                onChange={handleSetSendEmail}
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                              />
                            </div>
                            <div className="ml-3">
                              <label
                                htmlFor="email"
                                className="text-md font-medium text-gray-700"
                              >
                                Send Email to Job Creator
                              </label>
                              <p className="text-sm text-gray-500">
                                Send an email notification to the user that
                                created this job.
                              </p>
                            </div>
                          </div>

                          {sendEmail && userEmails.length === 0 && (
                            <div className="mt-3">
                              <p className="m-auto text-center text-sm text-gray-500">
                                No emails specified for the user that created
                                this job. You can add emails in the Team view.
                              </p>
                            </div>
                          )}

                          {sendEmail && userEmails.length > 0 && (
                            <div className="mt-4 rounded-lg bg-white px-5 py-4 ring-1 ring-gray-200">
                              <div className="mb-3 text-md">
                                Select which emails you want to use:
                              </div>
                              {userEmails.map((userEmail, index) => (
                                <div
                                  key={index}
                                  className="relative mb-3 flex items-start"
                                >
                                  <div className="flex h-5 items-center">
                                    <input
                                      id={"email" + index}
                                      checked={userEmail.selected}
                                      onChange={() =>
                                        handleUserEmailChange(userEmail.email)
                                      }
                                      type="checkbox"
                                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                    />
                                  </div>
                                  <div className="ml-3 cursor-pointer text-sm">
                                    <label
                                      htmlFor={"email" + index}
                                      className="text-gray-700"
                                    >
                                      {userEmail.email}
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                          <div className="flex items-start">
                            <div className="mt-0.5 flex h-5 items-center">
                              <input
                                id="emailProjectManager"
                                name="emailProjectManager"
                                checked={sendEmailToProjectManager}
                                onChange={handleSetSendEmailToProjectManager}
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                              />
                            </div>
                            <div className="ml-3">
                              <label
                                htmlFor="emailProjectManager"
                                className="text-md font-medium text-gray-700"
                              >
                                Send Email to Assigned Project Managers
                              </label>
                              <p className="text-sm text-gray-500">
                                Send an email notification to the PMs assigned
                                to this job.
                              </p>
                            </div>
                          </div>

                          {sendEmailToProjectManager &&
                            projectManagerEmails.length === 0 && (
                              <div className="mt-3">
                                <p className="m-auto text-center text-sm text-gray-500">
                                  No emails specified for assigned project
                                  managers. You can add emails in the Team view.
                                </p>
                              </div>
                            )}

                          {sendEmailToProjectManager &&
                            projectManagerEmails.length > 0 && (
                              <div className="mt-4 rounded-lg bg-white px-5 py-4 ring-1 ring-gray-200">
                                <div className="mb-3 text-md">
                                  Select which emails you want to use:
                                </div>
                                {projectManagerEmails.map(
                                  (userEmail, index) => (
                                    <div
                                      key={index}
                                      className="relative mb-3 flex items-start"
                                    >
                                      <div className="flex h-5 items-center">
                                        <input
                                          id={"emailProjectManager" + index}
                                          checked={userEmail.selected}
                                          onChange={() =>
                                            handleProjectManagerEmailChange(
                                              userEmail.email,
                                            )
                                          }
                                          type="checkbox"
                                          className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                        />
                                      </div>
                                      <div className="ml-3 cursor-pointer text-sm">
                                        <label
                                          htmlFor={
                                            "emailProjectManager" + index
                                          }
                                          className="text-gray-700"
                                        >
                                          {userEmail.email}
                                        </label>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                          <div className="flex items-start">
                            <div className="mt-0.5 flex h-5 items-center">
                              <input
                                id="emailFollower"
                                name="emailFollower"
                                checked={sendEmailToFollowers}
                                onChange={handleSetSendFollowersEmail}
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                              />
                            </div>
                            <div className="ml-3">
                              <label
                                htmlFor="emailFollower"
                                className="text-md font-medium text-gray-700"
                              >
                                Send Email to Followers
                              </label>
                              <p className="text-sm text-gray-500">
                                Send an email notification to the selected
                                followers.
                              </p>
                            </div>
                          </div>

                          {sendEmailToFollowers &&
                            followerEmails.length === 0 && (
                              <div className="mt-3">
                                <p className="m-auto text-center text-sm text-gray-500">
                                  No emails specified for any followers.
                                </p>
                              </div>
                            )}

                          {sendEmailToFollowers &&
                            followerEmails.length > 0 && (
                              <div className="mt-4 rounded-lg bg-white px-5 py-4 ring-1 ring-gray-200">
                                <div className="mb-3 text-md">
                                  Select which emails you want to use:
                                </div>
                                {followerEmails.map((userEmail, index) => (
                                  <div
                                    key={index}
                                    className="relative mb-3 flex items-start"
                                  >
                                    <div className="flex h-5 items-center">
                                      <input
                                        id={"emailFollower" + index}
                                        checked={userEmail.selected}
                                        onChange={() =>
                                          handleFollowerEmailChange(
                                            userEmail.email,
                                          )
                                        }
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                      />
                                    </div>
                                    <div className="ml-3 cursor-pointer text-sm">
                                      <label
                                        htmlFor={"emailFollower" + index}
                                        className="text-gray-700"
                                      >
                                        {userEmail.email}
                                      </label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>}

          {embedded && activeTab === "photos" && (
            <JobPhotosWorkspace
              jobId={jobId}
              currentUser={currentUser}
              jobStatus={jobStatus}
              onCountChange={setCurrentPhotosCount}
              toolbarTarget={photoToolbarTarget}
            />
          )}

          <style>{`
  .comment-content {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #374151;
    padding: 0;
    min-height: auto;
  }

  .comment-content p {
    margin: 0 0 0.65rem 0;
  }

  .comment-content p:last-child {
    margin-bottom: 0;
  }

  .comment-content ul,
  .comment-content ol {
    padding-left: 1.25rem;
    margin: 0.5rem 0;
  }

  .comment-content ul {
    list-style-type: disc;
  }

  .comment-content ol {
    list-style-type: decimal;
  }

  .comment-content h1,
  .comment-content h2,
  .comment-content h3 {
    color: #111827;
    font-weight: 600;
    margin: 0.25rem 0 0.5rem 0;
  }

  .comment-content a {
    color: #0284c7;
    text-decoration: underline;
  }
`}</style>
        </div>
      )}

      {isDeleteCommentModalOpen && (
        <DeleteCommentModal
          isOpen={isDeleteCommentModalOpen}
          handleClose={handleToggleDeleteCommentModal}
          deleteComment={deleteComment}
          comment={commentToBeDeleted}
        />
      )}
    </AnimatedPage>
  );
};

export default JobComments;
